import { SUPPORTED_LANGUAGES } from '@inference.engine/widgets/web';
import { i18n } from '@/shared/i18n';

// Getters, not values: the array is built while the module is evaluated, so resolving per read
// is what lets a label follow a language change instead of freezing at import.
/* eslint-disable i18next/no-literal-string -- response style option wire values */
export const SUPPORTED_RESPONSE_STYLE = [
  {
    get label() {
      return i18n.t('chats:chats.copyGenerationOptions.responseStyleFocused');
    },
    value: 'focused',
  },
  {
    get label() {
      return i18n.t('chats:chats.copyGenerationOptions.responseStyleBalanced');
    },
    value: 'balanced',
  },
  {
    get label() {
      return i18n.t('chats:chats.copyGenerationOptions.responseStyleImaginative');
    },
    value: 'imaginative',
  },
] as const;
/* eslint-enable i18next/no-literal-string */

export const getDefaultTranslationOptions = (model: string): TranslationOptions => ({
  model,
  targetLanguages: [],
  // eslint-disable-next-line i18next/no-literal-string -- tone of voice wire value
  toneOfVoice: 'balanced',
  targetDemographic: '',
  responseStyle: 'balanced' as const,
});

export const SUPPORTED_LANGUAGE_OPTIONS = [...SUPPORTED_LANGUAGES]
  // eslint-disable-next-line i18next/no-literal-string -- Intl.Collator locale and sensitivity option
  .sort((a, b) => a.language.localeCompare(b.language, 'en', { sensitivity: 'base' }))
  .map((l) => ({
    label: l.countryCode ? `${l.language}, ${l.countryCode}` : l.language,
    value: `${l.languageCode}_${l.countryCode || 'WORLD'}`,
  }));

export type ResponseStyle = (typeof SUPPORTED_RESPONSE_STYLE)[number]['value'];

export type TranslationOptions = {
  model: string;
  targetLanguages: string[];
  toneOfVoice?: string;
  targetDemographic?: string;
  responseStyle: ResponseStyle;
  // The chip-driven model (V2) is carried here; the popover does not own it.
  llmOptions?: {
    model?: string;
    temperature?: number;
    topP?: number;
    maxOutputTokens?: number;
  };
};

const DEFAULT_TOP_P = 1;
const DEFAULT_MAX_OUTPUT_TOKENS = 20000;

const RESPONSE_STYLE_TEMPERATURE: Record<ResponseStyle, number> = {
  focused: 0.1,
  balanced: 0.5,
  imaginative: 0.8,
};

// Legacy V1 transform: the V1 popup owns the model (`options.model`), since the V1
// ChatInput has no AI Model chip. It writes that model into `llmOptions.model`.
export const transformTranslationOptions = (options: TranslationOptions) => ({
  ...options,
  llmOptions: {
    temperature: RESPONSE_STYLE_TEMPERATURE[options.responseStyle],
    model: options.model,
    topP: DEFAULT_TOP_P,
    maxOutputTokens: DEFAULT_MAX_OUTPUT_TOKENS,
  },
});

// V2 transform: the model is owned solely by the AI Model chip, which writes
// `llmOptions.model`. This transform must PRESERVE that model and only layer in the
// sampling params derived from the response style. Deriving the model from
// `options.model` (as the legacy transform does) would clobber the chip selection
// with `undefined`, because the V2 popover has no model selector.
export const transformTranslationOptionsV2 = (options: TranslationOptions) => ({
  ...options,
  llmOptions: {
    ...options.llmOptions,
    temperature: RESPONSE_STYLE_TEMPERATURE[options.responseStyle],
    topP: DEFAULT_TOP_P,
    maxOutputTokens: DEFAULT_MAX_OUTPUT_TOKENS,
  },
});
